import { CaretLeftIcon, CaretRightIcon } from "@phosphor-icons/react";

/*
  The page control, shared by DataTable and by every hand-rolled list that grew
  past one screen. Extracted so a grid and a plain list can't end up wearing two
  different-looking pagers on the same page.

  The page window keeps first, last and the pages either side of the current
  one, collapsing the rest to an ellipsis — so the control stays the same width
  whether the list has 3 pages or 300.

  Renders nothing when there is only one page: a pager reading "1" is noise.
*/
export default function Pager({ page, pageCount, onPage, testIdPrefix = "list" }) {
  if (pageCount <= 1) return null;

  const pages = [];
  for (let n = 1; n <= pageCount; n++) {
    if (n === 1 || n === pageCount || Math.abs(n - page) <= 1) pages.push(n);
    else if (pages[pages.length - 1] !== "…") pages.push("…");
  }

  const arrowCls =
    "p-1.5 rounded-sm border border-border text-zinc-600 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors";

  return (
    <div className="flex items-center gap-1" data-testid={`${testIdPrefix}-pagination-bar`}>
      <button
        onClick={() => onPage(page - 1)}
        disabled={page <= 1}
        aria-label="Previous page"
        data-testid={`${testIdPrefix}-pagination-prev`}
        className={arrowCls}
      >
        <CaretLeftIcon size={12} />
      </button>
      {pages.map((n, idx) =>
        n === "…" ? (
          <span key={`gap${idx}`} className="px-1 text-xs text-zinc-400">{"…"}</span>
        ) : (
          <button
            key={n}
            onClick={() => onPage(n)}
            aria-current={n === page ? "page" : undefined}
            data-testid={`${testIdPrefix}-pagination-page-${n}`}
            className={`min-w-[26px] h-[26px] px-1 rounded-sm border font-mono text-xs transition-colors ${
              n === page
                ? "border-[#0176D3] bg-[#0176D3] text-white font-medium"
                : "border-border text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            {n}
          </button>
        )
      )}
      <button
        onClick={() => onPage(page + 1)}
        disabled={page >= pageCount}
        aria-label="Next page"
        data-testid={`${testIdPrefix}-pagination-next`}
        className={arrowCls}
      >
        <CaretRightIcon size={12} />
      </button>
    </div>
  );
}
